import { useLingui } from '@lingui/react/macro'
import { Modal } from 'antd'
import { useDocument } from 'features/chatbot/hooks/useDocument'
import { Citation } from 'features/chatbot/types/response'
import { DocumentPreview } from 'features/files'
import React, { useEffect } from 'react'
import { useResizeDetector } from 'react-resize-detector'

interface CitationDocumentModalProps {
  citation: Citation
  isOpen: boolean
  onClose: () => void
}

export const CitationDocumentModal: React.FC<CitationDocumentModalProps> = ({
  citation,
  isOpen,
  onClose,
}) => {
  const { t } = useLingui()
  const { document: citationDocument } = useDocument(citation.entryId)
  const bodyRef = React.useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      bodyRef.current = window.document.body
    }
  }, [])

  const { height: viewportHeight } = useResizeDetector({
    handleWidth: false,
    refreshMode: 'debounce',
    refreshRate: 100,
    targetRef: bodyRef,
  })

  const dimensions = React.useMemo(() => {
    const currentHeight = viewportHeight || window.innerHeight
    const modalHeightRatio = 0.9
    const padding = 100
    const contentHeight = currentHeight * modalHeightRatio - padding
    const pdfAspectRatio = 8.5 / 11
    const contentWidth = contentHeight * pdfAspectRatio
    const totalWidth = contentWidth

    return {
      width: Math.round(totalWidth),
      height: Math.round(contentHeight),
    }
  }, [viewportHeight])

  const modalTitle = citation.fileRefPath
    ? citation.fileRefPath.split('/').pop()
    : t`Document Preview`

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={dimensions.width}
      centered
      destroyOnHidden>
      <DocumentPreview
        document={citationDocument}
        height={dimensions.height}
        initialPage={citation.pageNumber}
      />
    </Modal>
  )
}
